import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';

import { SingleSelect } from '@/common/SingleSelect';
import { UploadButton } from '@/components/common';
import { queryClient } from '@/configuration/react-query';
import { IMAGE, TEXT_OVERLAY, UPLOAD_PICKER_TYPES, VIDEO } from '@/constants';
import { AssetScope, COLORS, DeviceScheduleContentType } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { useFileUpload } from '@/hooks';
import {
  useGetAllEvents,
  useGetDeviceScheduleByIdQuery,
  useUpdateDeviceScheduleContentMutation
} from '@/services';
import { Asset, SelectOptionProps } from '@/types';

import { useDeviceScheduleSelection } from '../hooks/useDeviceScheduleSelection';
import {
  DeviceScheduleUploadContent,
  EventAssetsPicker,
  OrganizationAssetsPicker
} from './components';

type PushTabProps = {
  deviceScheduleId: number;
  requestSlotInterruptionWarning: (action: () => void | Promise<void>) => boolean;
  refetchDeviceSchedules?: () => void;
};

export const PushTab = ({
  deviceScheduleId,
  requestSlotInterruptionWarning,
  refetchDeviceSchedules
}: PushTabProps) => {
  const [t] = useTranslation('common');
  const [search, setSearch] = useState('');
  const previousScheduleSelectionKeyRef = useRef<string>('');

  const { data: deviceSchedule } = useGetDeviceScheduleByIdQuery(deviceScheduleId);

  const { data: allEvents } = useGetAllEvents(
    100,
    search,
    'Name',
    true,
    true,
    undefined,
    true,
    undefined
  );

  const eventOptions = useMemo(() => {
    if (!allEvents?.pages) {
      return [];
    }

    return allEvents.pages.flatMap((page) =>
      (page.data?.items as Array<{ id: string | number; name: string }>).map((event) => ({
        value: event.id,
        label: event.name
      }))
    );
  }, [allEvents]);

  const { mutate: updateDeviceScheduleContent } = useUpdateDeviceScheduleContentMutation(
    queryClient,
    {
      onSuccess: () => {
        if (refetchDeviceSchedules) {
          refetchDeviceSchedules();
        }
        showToast(ToastType.SUCCESS, t('deviceScheduleUpdatedSuccessfully'));
      },
      onError: () => showToast(ToastType.ERROR, t('somethingWentWrong'))
    }
  );

  const sourceOptions: SelectOptionProps[] = useMemo(
    () => [
      {
        value: DeviceScheduleContentType.NewAsset,
        label: t('uploadNew')
      },
      {
        value: DeviceScheduleContentType.EventAsset,
        label: t('fromEvent')
      },
      {
        value: DeviceScheduleContentType.OrganizationAsset,
        label: t('fromAssets')
      }
    ],
    [t]
  );

  const {
    selectedSource,
    selectedEvent,
    selectedEventAssetId,
    selectedOrganizationAssetId,
    uploadedAsset,
    setSource,
    setEvent,
    assignAsset,
    resetSelectionFromSchedule
  } = useDeviceScheduleSelection(deviceSchedule?.data, sourceOptions, eventOptions);

  useEffect(() => {
    const scheduleSelectionKey = `${deviceSchedule?.data?.contentType ?? 'none'}-${
      deviceSchedule?.data?.assetId ?? 'none'
    }`;

    if (previousScheduleSelectionKeyRef.current === scheduleSelectionKey) {
      return;
    }

    previousScheduleSelectionKeyRef.current = scheduleSelectionKey;
    resetSelectionFromSchedule();
  }, [
    deviceSchedule?.data?.assetId,
    deviceSchedule?.data?.contentType,
    resetSelectionFromSchedule
  ]);

  const assignAssetToSchedule = (asset: Asset, contentType: DeviceScheduleContentType) => {
    if (deviceSchedule?.data.assetId === asset.id) {
      return;
    }
    updateDeviceScheduleContent({
      id: deviceScheduleId,
      payload: {
        assetId: asset.id,
        contentType
      }
    });

    assignAsset(asset, contentType);
  };

  const handleSourceChange = (newValue: SingleValue<SelectOptionProps>) => {
    setSource(newValue as SelectOptionProps | null);
  };

  const handleEventChange = (newValue: SingleValue<SelectOptionProps>) => {
    setEvent(newValue as SelectOptionProps | null);
  };

  const searchCallback = (inputValue: string) => {
    setSearch(inputValue);
  };

  const handleUploadedAsset = (asset: Asset) => {
    assignAssetToSchedule(asset, DeviceScheduleContentType.NewAsset);
  };

  const { uploadFiles, isUploading } = useFileUpload({
    pickerTypes: UPLOAD_PICKER_TYPES.IMAGE_VIDEO,
    isPublic: false,
    assetScope: AssetScope.PRIVATE,
    onCreatedAsset: handleUploadedAsset
  });

  const handleAssetAssigned = (asset: Asset) => {
    const contentType =
      (selectedSource?.value as DeviceScheduleContentType) ?? DeviceScheduleContentType.NewAsset;

    const warningQueued = requestSlotInterruptionWarning(() => {
      assignAssetToSchedule(asset, contentType);
    });

    if (warningQueued) {
      return;
    }

    assignAssetToSchedule(asset, contentType);
  };

  const handleFilesSelected = async (files: File[]) => {
    const uploadSelectedFiles = async () => {
      await uploadFiles(files);
    };

    const warningQueued = requestSlotInterruptionWarning(uploadSelectedFiles);

    if (warningQueued) {
      return;
    }

    await uploadSelectedFiles();
  };

  const sourceDropdown = (
    <div className='w-2/5'>
      <SingleSelect
        id='source-select'
        label={t('source')}
        options={sourceOptions}
        value={selectedSource}
        handleChange={handleSourceChange}
        placeholder={t('selectSource')}
        isClearable={true}
        textColor='var(--color-secondary-gray)'
        containerClassName={COLORS.SECONDARY_GRAY}
        dropdownIndicatorColor={COLORS.SECONDARY_GRAY}
      />
    </div>
  );

  return (
    <div className='flex size-full flex-col gap-4'>
      {selectedSource?.value === DeviceScheduleContentType.NewAsset && (
        <div className='flex w-full justify-between gap-4'>
          {sourceDropdown}
          <div className='flex items-end gap-2'>
            <UploadButton
              multiple
              text='uploadAsset'
              pickerTypes={UPLOAD_PICKER_TYPES.IMAGE_VIDEO}
              showMaxFileSize={false}
              isUploading={isUploading}
              disabled={isUploading}
              onFilesSelected={handleFilesSelected}
            />
          </div>
        </div>
      )}

      {selectedSource?.value === DeviceScheduleContentType.EventAsset && (
        <div className='flex justify-between gap-4'>
          {sourceDropdown}
          <div className='flex-1'>
            <SingleSelect
              id='event-select'
              label={t('event')}
              options={eventOptions}
              value={selectedEvent}
              handleChange={handleEventChange}
              handleInputChange={searchCallback}
              placeholder={t('selectEvent')}
              isClearable={true}
              textColor='var(--color-secondary-gray)'
              containerClassName='text-white'
              dropdownIndicatorColor={COLORS.SECONDARY_GRAY}
              isSearchable={true}
            />
          </div>
        </div>
      )}

      {selectedSource?.value !== DeviceScheduleContentType.NewAsset &&
        selectedSource?.value !== DeviceScheduleContentType.EventAsset &&
        sourceDropdown}

      {selectedSource?.value === DeviceScheduleContentType.NewAsset && (
        <DeviceScheduleUploadContent
          uploadedAsset={uploadedAsset}
          onAssetCreated={handleAssetAssigned}
        />
      )}

      {selectedSource?.value === DeviceScheduleContentType.EventAsset && (
        <div className='no-scrollbar flex-1 overflow-y-auto'>
          <EventAssetsPicker
            selectedEvent={selectedEvent}
            selectedAssetId={selectedEventAssetId}
            onAssetSelect={handleAssetAssigned}
          />
        </div>
      )}

      {selectedSource?.value === DeviceScheduleContentType.OrganizationAsset && (
        <div className='no-scrollbar flex-1 overflow-y-auto'>
          <OrganizationAssetsPicker
            selectedAssetId={selectedOrganizationAssetId}
            onAssetSelect={handleAssetAssigned}
            assetTypes={[VIDEO, IMAGE, TEXT_OVERLAY]}
            deviceScheduleId={deviceScheduleId}
          />
        </div>
      )}
    </div>
  );
};
